import { Dropdown } from 'antd'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import AIcon from '@/components/A/AIcon/AIcon'
import { I18N_LOCALES } from '@/i18n/script/consts'
import { useTypedLocalStorage } from '@/helpers/commonHooks/useTypedLocalStorage'
import styles from './SwitchLanguageOption.module.less'

type LanguageOption = {
  label: string
  value: string
  nativeName: string
}

function SwitchLanguageOption() {
  const { t, i18n } = useTranslation()
  const [, setStorageLanguage] = useTypedLocalStorage('adeff-language', {
    defaultValue: '',
  })
  const languageMenuAlign = {
    points: ['tl', 'tr'],
    offset: [8, 0],
    overflow: {
      adjustX: true,
      adjustY: true,
    },
  }

  const languageOptions: LanguageOption[] = useMemo(() => {
    return [
      {
        name: 'English',
        key: 'en',
        nativeName: 'English',
      },
      {
        name: 'Chinese',
        key: 'zh',
        nativeName: '简体中文',
      },
      {
        name: 'Traditional Chinese',
        key: 'zh-Hant',
        nativeName: '繁体中文',
      },
      ...I18N_LOCALES,
    ].map((locale) => {
      return {
        label: locale.name,
        value: locale.key,
        nativeName: locale.nativeName,
      }
    })
  }, [])

  const currentLanguageLabel = useMemo(() => {
    return languageOptions.find((option) => option.value === i18n.language)?.nativeName || ''
  }, [languageOptions, i18n.language])

  const handleLanguageChange = (lng: string) => {
    i18n.changeLanguage(lng)
    setStorageLanguage(lng)
  }

  const menuItems = languageOptions.map((option) => {
    return {
      key: option.value,
      label: (
        <div
          className={classNames(styles.languageMenuItem, {
            [styles.active]: option.value === i18n.language,
          })}
          data-testid={`nav-language-option-${option.value}`}
        >
          <span className={styles.languageMenuNativeName}>{option.nativeName}</span>
          <span className={styles.languageMenuLabel}>{t(`i18n.${option.label}`)}</span>
        </div>
      ),
    }
  })

  return (
    <div className={styles.switchLanguageOption}>
      <Dropdown
        overlayClassName={styles.languageMenu}
        menu={{ items: menuItems, onClick: (item) => handleLanguageChange(`${item.key}`) }}
        align={languageMenuAlign}
        trigger={['hover']}
        autoAdjustOverflow
      >
        <button
          type='button'
          className={classNames(styles.actionRow, styles.actionRowButton)}
          data-testid='nav-language-switch-btn'
        >
          <span className={styles.actionRowLeft}>
            <AIcon type='icon-yuyan' width={17} height={17} />
            {currentLanguageLabel}
          </span>
          <span className={styles.actionRowRight}>
            <AIcon type='icon-line-global_right' width={16} height={16} />
          </span>
        </button>
      </Dropdown>
    </div>
  )
}

export default SwitchLanguageOption
